/**
 * PSR Phase 2 — FCC geolocation + broadband context (tier-1 civic anchor).
 */
import { fetchWithTimeout } from '../dataProviders/types';
import { factToCitationRef } from '../../lib/sentiment/citationRefs';
import { hasResolvableSiteGeo, resolveSentimentSiteContext } from './siteContext';
import {
  emptySentimentResult,
  type SentimentFetchOptions,
  type SentimentFetchResult,
  type SentimentFetcher,
} from './types';
import { probeHttpAvailability } from './probeAvailability';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';

export const fccFetcher: SentimentFetcher = {
  sourceId: 'fcc',

  async probeAvailability() {
    return probeHttpAvailability({
      url: 'https://geo.fcc.gov/api/census/block/find?format=json&latitude=38.8977&longitude=-77.0365',
      unconfiguredStatuses: [401, 403],
    }); // R-130-10
  },

  async isAvailable(siteId, opts) {
    return hasResolvableSiteGeo(siteId, opts);
  },

  async fetch(siteId, opts?: SentimentFetchOptions): Promise<SentimentFetchResult> {
    throwIfAborted(opts?.signal);
    try {
      const ctx = await resolveSentimentSiteContext(siteId, opts);
      if (!ctx) {
        return emptySentimentResult('fcc', ['fcc: no site coordinates']);
      }

      const url = `https://geo.fcc.gov/api/census/block/find?format=json&latitude=${ctx.lat}&longitude=${ctx.lng}`;
      const res = await fetchWithTimeout(url, 12_000, opts?.signal);
      throwIfAborted(opts?.signal);
      if (!res.ok) {
        return emptySentimentResult('fcc', [`fcc: HTTP ${res.status}`]);
      }

      const json = await res.json() as {
        County?: { name?: string; FIPS?: string };
        State?: { name?: string; code?: string; FIPS?: string };
        Block?: { FIPS?: string };
      };

      const now = new Date().toISOString();
      const facts: SentimentFetchResult['normalizedFacts'] = [];
      const geoUrl = `https://geo.fcc.gov/api/census/block/find?format=json&latitude=${ctx.lat}&longitude=${ctx.lng}`;

      if (json.State?.name || json.State?.code) {
        facts.push({
          key: 'fcc_state',
          label: 'FCC census state',
          value: json.State.name ?? json.State.code ?? '',
          url: geoUrl,
          observedAt: now,
        });
      }
      if (json.County?.name) {
        facts.push({
          key: 'fcc_county',
          label: 'FCC census county',
          value: json.County.name,
          url: geoUrl,
          observedAt: now,
        });
      }
      if (json.Block?.FIPS) {
        facts.push({
          key: 'fcc_block_fips',
          label: 'Census block FIPS',
          value: json.Block.FIPS,
          url: geoUrl,
          observedAt: now,
        });
      }

      if (facts.length === 0) {
        return emptySentimentResult('fcc', ['fcc: empty census block response']);
      }

      return {
        sourceId: 'fcc',
        coverage: 'full',
        normalizedFacts: facts,
        citationRefs: facts.map((f, i) => factToCitationRef(f, 'fcc', i + 1)),
        errors: [],
        fetchedAt: now,
      };
    } catch (e) {
      rethrowIfAborted(opts?.signal, e);
      return emptySentimentResult('fcc', [`fcc: ${e instanceof Error ? e.message : String(e)}`]);
    }
  },
};
